import { Link } from "react-router";

import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * The Echoflicks smile icon (the same mark echoflicks.com ships as its
 * favicon), inlined so the header never waits on an asset request and never
 * inherits a stale logo path from a persisted configuration row.
 *
 * The glyph sits on a white rounded tile on purpose: the CRM defaults to a dark
 * shell and the mark is designed for a light ground.
 */
const SMILE_PATHS = [
  "M97.68,0C43.82,0,0,43.82,0,97.68s43.82,97.68,97.68,97.68,97.68-43.82,97.68-97.68S151.54,0,97.68,0ZM97.68,183.34c-47.23,0-85.66-38.43-85.66-85.66S50.45,12.02,97.68,12.02s85.66,38.43,85.66,85.66-38.43,85.66-85.66,85.66Z",
  "M67.48,80.15c7.8,0,14.15-6.35,14.15-14.15s-6.35-14.15-14.15-14.15-14.15,6.35-14.15,14.15,6.35,14.15,14.15,14.15Z",
  "M114.72,66c0,7.8,6.34,14.15,14.15,14.15s14.14-6.35,14.14-14.15-6.34-14.15-14.14-14.15-14.15,6.35-14.15,14.15Z",
  "M29.72,93.9h136.9c1.74,0,3.4-.76,4.54-2.07,1.14-1.32,1.65-3.06,1.41-4.79-5.21-36.77-37.2-64.51-74.41-64.51S28.98,50.27,23.77,87.04c-.24,1.73.27,3.47,1.42,4.79,1.14,1.31,2.79,2.07,4.53,2.07ZM147.53,58.4c.96,2.34,1.49,4.91,1.49,7.6,0,6.44-3.04,12.19-7.76,15.88-3.42,2.68-7.72,4.28-12.39,4.28s-8.98-1.6-12.4-4.28c-4.72-3.69-7.76-9.44-7.76-15.88,0-11.12,9.04-20.16,20.16-20.16,2.42,0,4.74.43,6.89,1.21,5.34,1.95,9.63,6.1,11.77,11.35ZM60.59,47.05c2.15-.78,4.47-1.21,6.89-1.21,11.11,0,20.16,9.04,20.16,20.16,0,6.44-3.04,12.2-7.77,15.88-3.43,2.69-7.73,4.28-12.39,4.28s-8.98-1.6-12.4-4.28c-4.72-3.69-7.76-9.44-7.76-15.88,0-2.68.52-5.23,1.48-7.57,2.13-5.27,6.44-9.43,11.79-11.38Z",
  "M167.46,101.51c-3.29-.46-6.33,1.82-6.79,5.11-4.37,30.89-31.25,54.19-62.51,54.19s-58.11-23.3-62.49-54.19c-.46-3.29-3.5-5.57-6.79-5.11-3.29.47-5.57,3.51-5.11,6.8,5.21,36.78,37.19,64.52,74.39,64.52s69.2-27.74,74.41-64.52c.47-3.29-1.82-6.33-5.11-6.8Z",
  "M50.2,101.58c-3.25.68-5.34,3.86-4.66,7.11,5.16,24.77,27.29,42.75,52.62,42.75s47.49-17.98,52.64-42.75c.68-3.25-1.41-6.43-4.66-7.11-3.26-.69-6.43,1.41-7.11,4.66-4,19.23-21.18,33.18-40.87,33.18s-36.85-13.95-40.85-33.18c-.68-3.25-3.86-5.35-7.11-4.66Z",
  "M98.16,130.04c13.33,0,25.13-7.99,30.06-20.35,1.23-3.09-.28-6.58-3.36-7.81-3.09-1.23-6.58.27-7.81,3.36-3.09,7.76-10.51,12.78-18.89,12.78s-15.78-5.02-18.87-12.78c-1.23-3.09-4.73-4.59-7.81-3.36-3.08,1.23-4.58,4.72-3.36,7.81,4.93,12.36,16.72,20.35,30.04,20.35Z",
];

export const EchoflicksIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 256 256"
    role="img"
    aria-hidden="true"
    focusable="false"
    className={cn("shrink-0", className)}
  >
    <rect
      x="1"
      y="1"
      width="254"
      height="254"
      rx="56"
      fill="#FFFFFF"
      stroke="#141519"
      strokeOpacity="0.08"
      strokeWidth="2"
    />
    <g transform="translate(40 40) scale(0.9009)" fill="#141519">
      {SMILE_PATHS.map((d) => (
        <path key={d.slice(0, 24)} d={d} />
      ))}
    </g>
  </svg>
);

/**
 * Brand lockup used by the desktop header and the mobile dashboard header:
 * the icon tile plus the configured CRM title. Links home.
 */
export const BrandMark = ({
  className,
  titleClassName,
}: {
  className?: string;
  titleClassName?: string;
}) => {
  const { title } = useConfigurationContext();
  return (
    <Link
      to="/"
      aria-label={title}
      className={cn(
        "flex shrink-0 items-center gap-2.5 text-secondary-foreground no-underline",
        className,
      )}
    >
      <EchoflicksIcon className="size-8 rounded-lg" />
      <span
        className={cn(
          "truncate text-lg font-semibold tracking-tight",
          titleClassName,
        )}
      >
        {title}
      </span>
    </Link>
  );
};
